import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@swyftx/aviary/atoms/Card';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Skeleton } from '@swyftx/aviary/atoms/Skeleton';
import { Numeric } from '@swyftx/aviary/atoms/Typography';
import { useTailwindBreakpoint } from '@swyftx/aviary/hooks/useTailwindBreakpoint';
import { InformationSimple } from '@swyftx/aviary/icons/outlined';
import { Gauge } from '@swyftx/aviary/icons/outlined/Gauge';
import { LoanHealthEducationModal } from '@swyftx/features/loans/components/LoanHealth/EducationModal/LoanHealthEducationModal';
import { LoanHealthGauge } from '@swyftx/features/loans/components/LoanHealth/LoanGuageGraphic/LoanHealthGauge';
import { CustomTooltip } from '@swyftx/features/loans/components/SingleLoansDashboard/CustomTooltip';
import { useSingleLoanHealthCard } from '@swyftx/features/loans/components/SingleLoansDashboard/api/hooks/useSingleLoanHealthCard';

import AssetIcon from '@global-components/AssetIcon/AssetIcon';

import { Loan } from '@shared/api/@types/loans';
import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { useCurrentPrice } from '@hooks/Trade/useCurrentPrice';

type Props = {
  className?: string;
  isFetchingLoan?: boolean;
  loanData?: Loan;
};

const SingleLoanHealthCard: React.FC<Props> = ({ isFetchingLoan, loanData }) => {
  const { t } = useTranslation('loans');
  const isXs = useTailwindBreakpoint('xs');

  const {
    fiatAsset,
    securityAsset,
    securityCryptoAmountFormatted,
    securityFiatAmountFormatted,
    loanToValue24hTrendPercentage,
    lvrPercentage,
    healthStatusColor,
    statusTitle,
    trendChangeColor,
  } = useSingleLoanHealthCard(loanData);

  const currentSecurityAssetPrice = Big(useCurrentPrice(fiatAsset, securityAsset, 'midPrice'));

  const statItem = (type: string, value: string): JSX.Element => (
    <div className='flex flex-col gap-4'>
      <Skeleton loading={isFetchingLoan}>
        <CustomTooltip
          title={t(`singleLoan.loanHealthCard.${type}.title` as unknown as TemplateStringsArray)}
          tooltip={t(`singleLoan.loanHealthCard.${type}.tooltip` as unknown as TemplateStringsArray)}
        />
      </Skeleton>

      <Skeleton loading={isFetchingLoan}>
        <Numeric
          size='large'
          weight='emphasis'
          color={type === '24hrChange' ? trendChangeColor : healthStatusColor}
          className='capitalize'
        >
          {value}
        </Numeric>
      </Skeleton>
    </div>
  );

  return (
    <Card className='w-full'>
      <CardHeader className='align-center flex-row justify-between'>
        <Skeleton loading={isFetchingLoan}>
          <FlexLayout alignItems='center' spacing={8}>
            <Gauge className='h-20 w-20' />
            <CardTitle className='flex gap-2 text-lg'>{t('singleLoan.loanHealthCard.title')}</CardTitle>
          </FlexLayout>
        </Skeleton>

        <Skeleton loading={isFetchingLoan}>
          <LoanHealthEducationModal>
            <Button layout='icon' leadingIcon={<InformationSimple className='h-20 w-20' />} variant='outlined' />
          </LoanHealthEducationModal>
        </Skeleton>
      </CardHeader>

      <CardContent>
        <div className='mb-24 mt-24'>
          <div className='mb-24 flex justify-center pb-24'>
            <Skeleton loading={isFetchingLoan}>
              <div className='mb-24 flex flex-col items-center pb-24'>
                <LoanHealthGauge
                  id={loanData?.loanId ?? 'health_gauge'}
                  healthPercentage={Number(lvrPercentage ?? 0)}
                  size={isXs ? 'sm' : 'lg'}
                  backgroundFillColor='var(--color-background-surface-primary)'
                />
              </div>
            </Skeleton>
          </div>

          <div className='mb-24 grid gap-12 sm:grid-cols-3'>
            {statItem('healthStatus', statusTitle)}
            {statItem('lvr', lvrPercentage ? `${lvrPercentage}%` : 'N/A')}
            {statItem('24hrChange', loanToValue24hTrendPercentage ? `${loanToValue24hTrendPercentage}%` : 'N/A')}
          </div>

          <FlexLayout direction='column' spacing={12}>
            <FlexLayout direction='row' justifyContent='space-between' alignItems='start'>
              <Skeleton loading={isFetchingLoan}>
                <CustomTooltip
                  title={t('singleLoan.loanHealthCard.securityAmount.title')}
                  tooltip={t('singleLoan.loanHealthCard.securityAmount.tooltip')}
                />
              </Skeleton>

              <div className='min-w-0'>
                <Skeleton loading={isFetchingLoan}>
                  <FlexLayout direction='column' justifyContent='end' alignItems='end'>
                    <FlexLayout direction='row' spacing={4}>
                      <AssetIcon asset={securityAsset} size={20} />
                      <Numeric size='medium' weight='emphasis' color='primary' className='break-words'>
                        {securityCryptoAmountFormatted}
                      </Numeric>
                    </FlexLayout>
                    <Numeric size='medium' color='secondary' className='break-words'>
                      {securityFiatAmountFormatted}
                    </Numeric>
                  </FlexLayout>
                </Skeleton>
              </div>
            </FlexLayout>

            <FlexLayout direction='row' justifyContent='space-between' alignItems='end'>
              <Skeleton loading={isFetchingLoan}>
                <CustomTooltip
                  title={t('singleLoan.loanHealthCard.currentBTCPrice.title')}
                  tooltip={t('singleLoan.loanHealthCard.currentBTCPrice.tooltip')}
                />
              </Skeleton>

              <div className='min-w-0 text-right'>
                <Skeleton loading={isFetchingLoan}>
                  <Numeric size='medium' weight='emphasis' color='primary' className='break-words'>
                    {formatCurrency(currentSecurityAssetPrice, fiatAsset, { appendCode: true })}
                  </Numeric>
                </Skeleton>
              </div>
            </FlexLayout>
          </FlexLayout>
        </div>

        <FlexLayout direction='row' spacing={8}>
          <Skeleton loading={isFetchingLoan}>
            <Button
              size={isXs ? 'md' : 'lg'}
              variant='filled'
              disabled={!loanData?.loanToValue}
              className={isXs ? 'w-full' : ''}
            >
              {t('singleLoan.loanHealthCard.actions.topUp')}
            </Button>
          </Skeleton>

          <Skeleton loading={isFetchingLoan}>
            <Button
              size={isXs ? 'md' : 'lg'}
              variant='outlined'
              disabled={!loanData?.loanToValue}
              className={isXs ? 'w-full' : ''}
            >
              {t('singleLoan.loanHealthCard.actions.withdraw')}
            </Button>
          </Skeleton>
        </FlexLayout>
      </CardContent>
    </Card>
  );
};

SingleLoanHealthCard.displayName = 'SingleLoanHealthCard';

export { SingleLoanHealthCard };
